import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Divider, Typography, styled } from '@mui/material'
import { useQuery } from '@tanstack/react-query'

import { useAppStore } from '@/store'
import { ApiResponse, ApplicationService } from '@/api/services/ApplicationService'
import { ILoanAssessment } from '@/types'
import { ErrorPage } from '@/pages/Error'
import { AssessmentRules } from './AssementRules'
import { CatchAllRules } from './CatchAllRules'
import { replaceNullValues } from '@/utils/common'

import SearchLogo from '@/assets/search.svg'
import { DecisionSummary } from './DecisionSummary'

type AssessmentCategories = {
  general: ILoanAssessment | null
  creditrisk: ILoanAssessment | null
  decision: ILoanAssessment | null
  refer: ILoanAssessment | null
  catchallrule: ILoanAssessment | null
}

export const Rules: React.FC = () => {
  const { selectedApplication } = useAppStore()

  const {
    data: rules,
    isLoading,
    isError,
  } = useQuery<ApiResponse<ILoanAssessment>, Error>({
    queryKey: ['eligibility-assessments', selectedApplication.id],
    queryFn: () => ApplicationService.fetchApplicationDetails(selectedApplication.id),
    enabled: !!selectedApplication?.id,
  })

  const [loanRulesByCategory, setLoanRulesByCategory] = useState<AssessmentCategories>({
    general: null,
    creditrisk: null,
    decision: null,
    refer: null,
    catchallrule: null,
  })

  const safeRules = replaceNullValues<ILoanAssessment[]>(rules?.eligibilityAssessments ?? [])

  useEffect(() => {
    if (!safeRules) return

    const newCategories: Partial<AssessmentCategories> = {}
    for (const category of Object.keys(loanRulesByCategory) as (keyof AssessmentCategories)[]) {
      newCategories[category] = safeRules.find(a => a.category === category) ?? null
    }
    setLoanRulesByCategory(prev => ({ ...prev, ...newCategories }))
  }, [rules])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} aria-label="Loading applications" />
      </Box>
    )
  }

  if (safeRules.length === 0) {
    return (
      <EmptyContainer>
        <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
        <Title>No Rules Found</Title>
      </EmptyContainer>
    )
  }

  if (isError) {
    return <ErrorPage />
  }

  return (
    <>
      {loanRulesByCategory.general && (
        <AssessmentRules title="General Eligibility Rules" assessment={loanRulesByCategory.general} />
      )}
      {loanRulesByCategory.general && <StyledDivider />}

      {loanRulesByCategory.creditrisk && (
        <AssessmentRules title="Credit Risk Score Card" assessment={loanRulesByCategory.creditrisk} />
      )}
      {loanRulesByCategory.creditrisk && <StyledDivider />}

      {loanRulesByCategory.decision && (
        <AssessmentRules title="Decision Rules" assessment={loanRulesByCategory.decision} />
      )}
      {loanRulesByCategory.decision && <StyledDivider />}

      {loanRulesByCategory.refer && (
        <AssessmentRules title="Refer Rules" assessment={loanRulesByCategory.refer} />
      )}
      {loanRulesByCategory.refer && <StyledDivider />}

      {loanRulesByCategory.catchallrule && (
        <CatchAllRules assessment={loanRulesByCategory.catchallrule} />
      )}
      {loanRulesByCategory.catchallrule && <StyledDivider />}
      {
        safeRules && <DecisionSummary loanRulesByCategory={loanRulesByCategory} />
      }
    </>
  )
}

const StyledDivider = styled(Divider)(() => ({
  borderColor: '#EAECF0',
  padding: '10px 0',
}))

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  marginTop: '3rem',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))